<template>
  <div class="c-user-menu flex">
    <NPopover
      ref="popoverRef"
      :placement="menuPlacement"
      :show-arrow="false"
      :style="{
        '--n-padding': 0,
      }"
      trigger="click"
    >
      <template #trigger>
        <div>
          <CUserAvatar
            class="cursor-pointer"
            :email="user?.email"
            :firstName="user?.first_name"
            :lastName="user?.last_name"
            :picture="user?.picture"
          />
        </div>
      </template>
      <div v-if="user" class="flex gap-3 pa-5">
        <CUserAvatar
          :email="user.email"
          :firstName="user.first_name"
          :lastName="user.last_name"
          :picture="user.picture"
        />

        <div class="flex flex-col gap-1">
          <span class="tracking-wide leading-none text-base">{{ `${user.first_name} ${user.last_name}` }}</span>
          <span class="tracking-wide leading-none text-sm">{{ user.email }}</span>
          <CInfoBadge
            v-if="isNonMemberRole(userRole)"
            :background-color="themeVars.primaryColor"
            :color="themeVars.baseColor"
            fa-type="fas"
            fit-content
            :icon="roleIcon"
            inline
            :text="userRole"
            variation="extended"
          />
        </div>
      </div>

      <NDivider class="!my-0" />

      <div class="flex flex-col gap-2 pa-3">
        <div
          v-for="option of options"
          :key="option.key"
          class="c-user-menu__item flex justify-between items-center gap-2 rounded-md"
          :class="{
            'c-user-menu__item--hoverable cursor-pointer': option.clickable,
            'pa-2': !option.paddingClass,
            [option.paddingClass ?? '']: !!option.paddingClass,
          }"
          @click="handleClick(option)"
        >
          <NDivider v-if="option.type === 'separator'" class="!my-0" />
          <template v-else>
            <span class="tracking-wide text-base grow shrink-0">{{ option.text }}</span>
            <CIcon v-if="option.icon" :icon="option.icon" />
            <component :is="option.component" v-if="option.component" class="basis-1/2" v-bind="option.props" />
          </template>
        </div>
      </div>

      <template v-if="instance?.name">
        <NDivider class="!my-0" />
        <div class="tracking-wide leading-none text-sm text-center color-text2 pa-5 flex items-center gap-2 uppercase">
          <CChip v-if="instance.environment" :color="instance.color || instance.environment?.color">
            {{ instance.environment.name }}
          </CChip>
          {{ instance.name }}
        </div>
      </template>
    </NPopover>
  </div>
</template>
<script lang="ts" setup>
import { ref, type Component } from 'vue'
import { NDivider, NPopover, type PopoverPlacement } from 'naive-ui'

import CUserAvatar from '@lib/components/CUserAvatar/CUserAvatar.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CChip from '@lib/components/CChip/CChip.vue'
import CInfoBadge from '@lib/components/CInfoBadge/CInfoBadge.vue'
import { useThemeVars } from 'naive-ui'
import { isNonMemberRole } from '@lib/constants/roles.const'
import type { User, Instance } from '@openapi'

interface UserMenuOptionItem {
  clickable?: boolean
  component?: Component
  key: string
  props?: Record<string, any>
  icon?: string
  text?: string
  type?: 'separator'
  paddingClass?: string
}

interface Props {
  menuPlacement: PopoverPlacement
  options?: UserMenuOptionItem[]
  user?: User | null
  instance?: Instance
  userRole?: string
  roleIcon?: string
}

const { menuPlacement = 'top', options = [], user = null, userRole, roleIcon } = defineProps<Props>()

const emit = defineEmits<{
  action: [key: string]
}>()

defineExpose({
  setShow: (show: boolean) => popoverRef.value?.setShow(show),
})

const themeVars = useThemeVars()

const popoverRef = ref<InstanceType<typeof NPopover> | null>(null)

function handleClick(option: UserMenuOptionItem) {
  if (option.clickable) popoverRef.value?.setShow(false)
  emit('action', option.key)
}
</script>

<style scoped>
:deep(.c-user-menu__item--hoverable:hover) {
  background-color: var(--p-primary6);
}
</style>
